'use client'

import * as m from 'motion/react-m'
import { Motion } from '@/lib/motion/lazy-motion'
import { useEffect, useRef, useState } from 'react'

import AppIcon from '@/components/content/app/AppIcon'

import { getBrandColors, cn } from '@/lib/utils'
import type { App } from '@/payload-types'

type BestMarqueeProps = {
  apps: App[]
  theme?: 'dark' | 'light'
  className?: string
}

export default function BestMarquee({ apps, theme = 'dark', className }: BestMarqueeProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const measureRef = useRef<HTMLDivElement>(null)
  const [needsMarquee, setNeedsMarquee] = useState(false)

  useEffect(() => {
    const checkOverflow = () => {
      if (containerRef.current && measureRef.current) {
        const containerWidth = containerRef.current.offsetWidth
        const contentWidth = measureRef.current.scrollWidth
        setNeedsMarquee(contentWidth > containerWidth - 80)
      }
    }

    checkOverflow()
    const resizeObserver = new ResizeObserver(checkOverflow)
    if (containerRef.current) resizeObserver.observe(containerRef.current)
    window.addEventListener('resize', checkOverflow)

    return () => {
      resizeObserver.disconnect()
      window.removeEventListener('resize', checkOverflow)
    }
  }, [apps])

  const duplicatedApps = needsMarquee ? [...apps, ...apps] : apps

  return (
    <div
      ref={containerRef}
      className={cn(
        'relative w-full overflow-hidden',
        needsMarquee &&
          `before:pointer-events-none before:absolute before:inset-y-0 before:left-0 before:z-10 before:h-full before:w-[200px] before:bg-gradient-to-r before:to-transparent after:pointer-events-none after:absolute after:inset-y-0 after:right-0 after:z-10 after:h-full after:w-[200px] after:bg-gradient-to-l after:to-transparent ${theme === 'light' ? 'before:from-white after:from-white dark:before:from-black dark:after:from-black' : 'before:from-black after:from-black'}`,
        className,
      )}
    >
      {/* Hidden div to measure base content width */}
      <div className="absolute -left-[9999px]" aria-hidden="true">
        <div ref={measureRef} className="flex items-center gap-7">
          {apps.map((app) => {
            const { backgroundColor } = getBrandColors(app)
            return (
              <AppIcon
                key={`hidden-${app.id}`}
                icon={app.icon}
                size={56}
                invert={theme === 'dark'}
                background={backgroundColor}
              />
            )
          })}
        </div>
      </div>

      {/* Visible content */}
      <div className={cn('flex', !needsMarquee && 'justify-center')}>
        <Motion>
          <m.div
            ref={contentRef}
            className="flex items-center gap-7"
            animate={needsMarquee ? { x: ['-0%', '-50%'] } : { x: 0 }}
            transition={
              needsMarquee
                ? {
                    duration: 180,
                    ease: 'linear',
                    repeat: Infinity,
                  }
                : undefined
            }
          >
            {duplicatedApps.map((app, index) => {
              if (!app.slug) return null
              const { backgroundColor } = getBrandColors(app)
              return (
                <AppIcon
                  key={`${app.id}-${index}`}
                  icon={app.icon}
                  glyph={app.glyph}
                  name={app.name}
                  slug={app.slug}
                  size={56}
                  invert={true}
                  background={backgroundColor}
                  className="cursor-pointer"
                />
              )
            })}
          </m.div>
        </Motion>
      </div>
    </div>
  )
}
